import { useMemo, useState } from 'react'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui'
import { Popover, PopoverContent, PopoverTrigger } from '../ui'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Switch } from '../ui/switch'

export interface GroupOption {
  groupId: string
  name: string
  status: 'ACTIVE' | 'DEACTIVATED'
}

export interface ParentCompanyOption {
  id: string
  name: string
  groupId: string
  parentCompanyId?: string | null
}

export interface CreateCompanyInput {
  company: string
  groupId: string
  tradeType: 'CRYPTO' | 'CRYPTO_AND_FIAT' | 'DEACTIVATED'
  parentCompanyId?: string
  autoApprove?: boolean
}

interface CreateCompanyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groups: GroupOption[]
  parentCompanyCandidates?: ParentCompanyOption[]
  loading?: boolean
  onSubmit: (input: CreateCompanyInput) => Promise<void>
  preSelectedGroup?: GroupOption
  /** Admin-only: show auto-approve deposits toggle. Defaults off for user/valiant create. */
  showAutoApproveToggle?: boolean
}

export function CreateCompanyModal({
  open,
  onOpenChange,
  groups,
  parentCompanyCandidates = [],
  loading = false,
  onSubmit,
  preSelectedGroup,
  showAutoApproveToggle = false,
}: CreateCompanyModalProps) {
  const [company, setCompany] = useState('')
  const [selectedGroupId, setSelectedGroupId] = useState<string>(preSelectedGroup?.groupId || '')
  const [selectedParentCompanyId, setSelectedParentCompanyId] = useState<string>('')
  const [tradeType, setTradeType] = useState<'CRYPTO' | 'CRYPTO_AND_FIAT' | 'DEACTIVATED'>(
    'DEACTIVATED',
  )
  const [autoApprove, setAutoApprove] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [comboboxOpen, setComboboxOpen] = useState(false)

  const selectedGroup =
    preSelectedGroup || groups.find((group) => group.groupId === selectedGroupId)
  const isGroupPreSelected = !!preSelectedGroup

  const eligibleParentCompanies = useMemo(
    () =>
      parentCompanyCandidates.filter(
        (candidate) => candidate.groupId === selectedGroupId && !candidate.parentCompanyId,
      ),
    [parentCompanyCandidates, selectedGroupId],
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (company && selectedGroup && !isSubmitting) {
      setIsSubmitting(true)
      try {
        await onSubmit({
          company,
          groupId: selectedGroup.groupId,
          tradeType,
          parentCompanyId: selectedParentCompanyId || undefined,
          ...(showAutoApproveToggle ? { autoApprove } : {}),
        })
        // Reset form on success
        setCompany('')
        if (!isGroupPreSelected) {
          setSelectedGroupId('')
        }
        setSelectedParentCompanyId('')
        setTradeType('DEACTIVATED')
        setAutoApprove(false)
        onOpenChange(false)
      } catch (_error) {
        // Error handling is done by parent component
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  // Reset form when modal opens/closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setCompany('')
      if (!isGroupPreSelected) {
        setSelectedGroupId('')
      }
      setTradeType('DEACTIVATED')
      setSelectedParentCompanyId('')
      setAutoApprove(false)
      setSearchValue('')
      setComboboxOpen(false)
    }
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create Company</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="company">Name</Label>
            <Input
              id="company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Enter company name"
              required
              disabled={isSubmitting || loading}
            />
            <p className="text-sm text-muted-foreground">
              *You will need to upload files to be able to trade
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="group">Assign to group</Label>
            {isGroupPreSelected ? (
              <div className="flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
                <div>
                  <div className="font-medium">{selectedGroup?.name}</div>
                </div>
                <div className="text-sm text-muted-foreground">Pre-selected</div>
              </div>
            ) : (
              <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={comboboxOpen}
                    className="justify-between"
                    disabled={isSubmitting || loading}
                  >
                    {selectedGroup ? selectedGroup.name : 'Select a group...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search groups..."
                      value={searchValue}
                      onValueChange={setSearchValue}
                    />
                    <CommandList>
                      <CommandEmpty>No groups found.</CommandEmpty>
                      <CommandGroup>
                        {groups
                          .filter((group) =>
                            group.name.toLowerCase().includes(searchValue.toLowerCase()),
                          )
                          .map((group) => (
                            <CommandItem
                              key={group.groupId}
                              value={`${group.name} ${group.status}`}
                              onSelect={() => {
                                const nextGroupId =
                                  group.groupId === selectedGroupId ? '' : group.groupId
                                setSelectedGroupId(nextGroupId)
                                setSelectedParentCompanyId('')
                                setComboboxOpen(false)
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  selectedGroupId === group.groupId ? 'opacity-100' : 'opacity-0',
                                )}
                              />
                              <div>
                                <div className="font-medium">{group.name}</div>
                              </div>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {selectedGroupId && (
            <div className="space-y-2">
              <Label htmlFor="parentCompany">Parent Company</Label>
              <Select
                value={selectedParentCompanyId || 'none'}
                onValueChange={(value) => setSelectedParentCompanyId(value === 'none' ? '' : value)}
                disabled={isSubmitting || loading}
              >
                <SelectTrigger id="parentCompany">
                  <SelectValue placeholder="Select a parent company (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {eligibleParentCompanies.map((parentCompany) => (
                    <SelectItem key={parentCompany.id} value={parentCompany.id}>
                      {parentCompany.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Selecting a parent makes this a profile company that inherits KYB documents.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="tradeType">Trade Type</Label>
            <Select
              value={tradeType}
              onValueChange={(value) =>
                setTradeType(value as 'CRYPTO' | 'CRYPTO_AND_FIAT' | 'DEACTIVATED')
              }
              required
              disabled={isSubmitting || loading}
            >
              <SelectTrigger id="tradeType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CRYPTO">Crypto</SelectItem>
                <SelectItem value="CRYPTO_AND_FIAT">Crypto & Fiat</SelectItem>
                <SelectItem value="DEACTIVATED">Deactivated</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {showAutoApproveToggle && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Switch
                  id="autoApprove"
                  checked={autoApprove}
                  onCheckedChange={setAutoApprove}
                  disabled={isSubmitting || loading}
                />
                <Label htmlFor="autoApprove" className="cursor-pointer">
                  Auto-approve deposits
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                When enabled, deposits from this company will be automatically approved without
                manual review.
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              variant="main_action"
              className="w-full rounded-md"
              type="submit"
              disabled={!company || !selectedGroup || isSubmitting || loading}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
